import { useState } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import {
  AppleLoginButton,
  GoogleLoginButton,
  MicrosoftLoginButton,
} from 'react-social-login-buttons';
import { Auth, CognitoHostedUIIdentityProvider } from '@aws-amplify/auth';
import authService, {
  isInIframe,
  isPopupLoginError,
} from '../../../services/authService';

const PREFIX = 'SocialButtons';

const classes = {
  container: `${PREFIX}-container`,
};

const Root = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    width: '100%',
  },
}));

export default function SocialButtons() {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const [error, setError] = useState<string | null>(null);

  /**
   * Social identity providers refuse to be framed just like the SAML provider
   * does, so inside an iframe the flow has to run in a popup. Each of these
   * handlers is a click, i.e. a user gesture, which is what allows
   * window.open to succeed.
   */
  const signIn = (customProvider: string) => {
    if (!isInIframe()) {
      Auth.federatedSignIn({ customProvider });
      return;
    }
    setError(null);
    authService.loginFederatedPopup(customProvider).catch((err) => {
      setError(
        isPopupLoginError(err) && err.code === 'POPUP_BLOCKED'
          ? t('login.popupBlocked')
          : t('login.popupFailed'),
      );
    });
  };

  const inlineButtonStyle = {
    width: '100%',
    fontSize: '16px',
    fontWeight: 600,
    display: 'flex',
    justifyContent: 'center',
    boxShadow: theme.shadows[2],
    fontFamily: 'Source Sans Pro',
    backgroundColor: theme.palette.background.default,
    color: theme.palette.lightTextPrimary.main,
  };

  const activeStyle = {
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.lightTextPrimary.main,
  };

  return (
    <Root className={classes.container}>
      <GoogleLoginButton
        text={t('member.continueWithGoogle')}
        onClick={() => signIn(CognitoHostedUIIdentityProvider.Google)}
        style={inlineButtonStyle}
      />

      <MicrosoftLoginButton
        text={t('member.continueWithMicrosoft')}
        onClick={() => signIn('Microsoft')}
        style={inlineButtonStyle}
        activeStyle={activeStyle}
      />

      <AppleLoginButton
        text={t('member.continueWithApple')}
        onClick={() => signIn(CognitoHostedUIIdentityProvider.Apple)}
        style={inlineButtonStyle}
      />

      {error && (
        <Typography role="alert" color="error">
          {error}
        </Typography>
      )}
    </Root>
  );
}
